import type { BlendMode, FillGradient, Glow, Grade, GradientStop, Layer, Scene, Shadow, Vignette } from '../domain/schemas';

/**
 * Look v1 (Phase 5B.1): pure helpers shared by the renderer, the editor and the tests —
 * the colour-grade matrix, gradient geometry and colour stops, and the presets offered in
 * the editor. Nothing here touches Pixi.
 */

// ---------------------------------------------------------------- Colours

const clamp01 = (v: number) => Math.min(1, Math.max(0, v));
const hexRgb = (hex: string): [number, number, number] => [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16)) as [number, number, number];
const toHex = (rgb: readonly number[]) => `#${rgb.map((v) => Math.round(Math.min(255, Math.max(0, v))).toString(16).padStart(2, '0')).join('')}`;

/** Mixes two #rrggbb colours (t = 0 → a, 1 → b). */
export const mixHex = (a: string, b: string, t: number): string => {
  const x = hexRgb(a);
  const y = hexRgb(b);
  return toHex(x.map((v, i) => v + (y[i]! - v) * clamp01(t)));
};

/** A gradient stop as a CSS colour with alpha (what Pixi's FillGradient accepts). */
export const stopColor = (s: Pick<GradientStop, 'color' | 'opacity'>): string => {
  const [r, g, b] = hexRgb(s.color);
  return `rgba(${r}, ${g}, ${b}, ${clamp01(s.opacity)})`;
};

// ---------------------------------------------------------------- Gradients

/** Start and end of a linear gradient in a unit box (0 = left → right, 90 = top → bottom). */
export function linearPoints(angle: number): { start: { x: number; y: number }; end: { x: number; y: number } } {
  const a = (angle * Math.PI) / 180;
  const dx = Math.cos(a) / 2;
  const dy = Math.sin(a) / 2;
  return { start: { x: 0.5 - dx, y: 0.5 - dy }, end: { x: 0.5 + dx, y: 0.5 + dy } };
}

/**
 * The colour (and opacity) of a gradient at a point of the unit box: the reference the
 * renderer must match (used by tests; Pixi draws the gradient itself).
 */
export function gradientAt(g: FillGradient, x: number, y: number): { rgb: [number, number, number]; opacity: number } {
  let t: number;
  if (g.type === 'linear') {
    const { start, end } = linearPoints(g.angle);
    const vx = end.x - start.x;
    const vy = end.y - start.y;
    t = ((x - start.x) * vx + (y - start.y) * vy) / (vx * vx + vy * vy);
  } else {
    t = Math.hypot(x - g.cx, y - g.cy) / g.radius;
  }
  t = clamp01(t);
  const stops = g.stops;
  if (t <= stops[0]!.offset) return { rgb: hexRgb(stops[0]!.color), opacity: stops[0]!.opacity };
  for (let i = 1; i < stops.length; i++) {
    const a = stops[i - 1]!;
    const b = stops[i]!;
    if (t <= b.offset) {
      const k = b.offset === a.offset ? 1 : (t - a.offset) / (b.offset - a.offset);
      const x0 = hexRgb(a.color);
      const x1 = hexRgb(b.color);
      return { rgb: x0.map((v, j) => v + (x1[j]! - v) * k) as [number, number, number], opacity: a.opacity + (b.opacity - a.opacity) * k };
    }
  }
  const last = stops[stops.length - 1]!;
  return { rgb: hexRgb(last.color), opacity: last.opacity };
}

// ---------------------------------------------------------------- Colour grade

export const NEUTRAL_GRADE: Grade = Object.freeze({ brightness: 1, contrast: 1, saturation: 1, tint: '#ffffff', tintAmount: 0 });

export const isNeutralGrade = (g: Grade | null | undefined): boolean =>
  !g || (g.brightness === 1 && g.contrast === 1 && g.saturation === 1 && (g.tintAmount === 0 || g.tint.toLowerCase() === '#ffffff'));

/** Rec. 709 luminance weights. */
const LUMA = [0.2126, 0.7152, 0.0722] as const;

type Affine = number[]; // 4×5, row-major, offsets in 0–1 (Pixi's ColorMatrixFilter convention)

const identity = (): Affine => [1, 0, 0, 0, 0, 0, 1, 0, 0, 0, 0, 0, 1, 0, 0, 0, 0, 0, 1, 0];

/** a ∘ b: apply b first, then a. */
function compose(a: Affine, b: Affine): Affine {
  const out: Affine = new Array<number>(20).fill(0);
  for (let r = 0; r < 4; r++) {
    for (let c = 0; c < 5; c++) {
      let v = 0;
      for (let k = 0; k < 4; k++) v += a[r * 5 + k]! * b[k * 5 + c]!;
      if (c === 4) v += a[r * 5 + 4]!;
      out[r * 5 + c] = v;
    }
  }
  return out;
}

const diag = (r: number, g: number, b: number, offset = 0): Affine => [r, 0, 0, 0, offset, 0, g, 0, 0, offset, 0, 0, b, 0, offset, 0, 0, 0, 1, 0];

/**
 * The 4×5 colour matrix of a grade, in Pixi's `ColorMatrixFilter` convention (applied to
 * un-premultiplied RGBA in 0–1; offsets in 0–1). Order: brightness, contrast (around
 * mid-grey), saturation (around Rec. 709 luminance), then the tint mix. A neutral grade is
 * the identity.
 */
export function gradeMatrix(g: Grade): number[] {
  let m = identity();
  m = compose(diag(g.brightness, g.brightness, g.brightness), m);
  m = compose(diag(g.contrast, g.contrast, g.contrast, 0.5 * (1 - g.contrast)), m);
  const s = g.saturation;
  const sat: Affine = [];
  for (let r = 0; r < 3; r++) {
    for (let c = 0; c < 3; c++) sat.push((1 - s) * LUMA[c]! + (r === c ? s : 0));
    sat.push(0, 0);
  }
  sat.push(0, 0, 0, 1, 0);
  m = compose(sat, m);
  const [tr, tg, tb] = hexRgb(g.tint).map((v) => v / 255) as [number, number, number];
  const a = g.tintAmount;
  m = compose(diag(1 - a + a * tr, 1 - a + a * tg, 1 - a + a * tb), m);
  return m;
}

/** Applies a grade to an opaque colour exactly as the filter does (0–255, clamped). Tests use it as the reference. */
export function applyGrade(rgb: readonly number[], g: Grade): [number, number, number] {
  const m = gradeMatrix(g);
  const c = [rgb[0]! / 255, rgb[1]! / 255, rgb[2]! / 255, 1];
  return [0, 1, 2].map((r) => clamp01(m[r * 5]! * c[0]! + m[r * 5 + 1]! * c[1]! + m[r * 5 + 2]! * c[2]! + m[r * 5 + 3]! * c[3]! + m[r * 5 + 4]!) * 255) as [number, number, number];
}

// ---------------------------------------------------------------- Vignette

/** Radius (in the scene's unit box) at which the vignette reaches full `amount`: just past the corners. */
export const VIGNETTE_RADIUS = 0.75;

/** Colour stops of the vignette's radial gradient (transparent centre → `amount` at the corners). */
export function vignetteStops(v: Vignette): { offset: number; color: string }[] {
  const inner = Math.max(0, Math.min(0.95, 1 - v.softness));
  return [
    { offset: 0, color: stopColor({ color: v.color, opacity: 0 }) },
    { offset: inner, color: stopColor({ color: v.color, opacity: 0 }) },
    { offset: 1, color: stopColor({ color: v.color, opacity: v.amount }) },
  ];
}

export const isVisibleVignette = (v: Vignette | null | undefined): v is Vignette => !!v && v.amount > 0;

// ---------------------------------------------------------------- Budgets

/** Objects in a scene that draw a glow or a shadow (each is a blurred render pass). */
export const effectObjects = (scene: Pick<Scene, 'objects'>): number => scene.objects.filter((o) => o.glow || o.shadow).length;

// ---------------------------------------------------------------- Layers

export const BLEND_LABELS: Record<BlendMode, string> = { normal: 'Normal', add: 'Add (lighten)', screen: 'Screen', multiply: 'Multiply' };

/** Short badge for a layer's composition in the layer list ("" when it is at the default). */
export function layerLookBadge(layer: Pick<Layer, 'opacity' | 'blend'>): string {
  const parts: string[] = [];
  if (layer.blend && layer.blend !== 'normal') parts.push(layer.blend);
  if (layer.opacity !== undefined && layer.opacity < 1) parts.push(`${Math.round(layer.opacity * 100)}%`);
  return parts.join(' · ');
}

// ---------------------------------------------------------------- Presets

export interface Preset<T> {
  id: string;
  label: string;
  make: (base: string) => T;
}

/** Gradient presets; `base` is the shape's current fill colour. */
export const GRADIENT_PRESETS: Preset<FillGradient>[] = [
  {
    id: 'fade-down',
    label: 'Fade down',
    make: (c) => ({ type: 'linear', angle: 90, stops: [{ offset: 0, color: c, opacity: 1 }, { offset: 1, color: c, opacity: 0 }] }),
  },
  {
    id: 'shine',
    label: 'Shine',
    make: (c) => ({ type: 'linear', angle: 90, stops: [{ offset: 0, color: mixHex(c, '#ffffff', 0.45), opacity: 1 }, { offset: 0.5, color: c, opacity: 1 }, { offset: 1, color: mixHex(c, '#000000', 0.45), opacity: 1 }] }),
  },
  {
    id: 'radial-glow',
    label: 'Radial glow',
    make: (c) => ({ type: 'radial', cx: 0.5, cy: 0.5, radius: 0.5, stops: [{ offset: 0, color: mixHex(c, '#ffffff', 0.6), opacity: 1 }, { offset: 0.45, color: c, opacity: 1 }, { offset: 1, color: c, opacity: 0 }] }),
  },
  {
    id: 'night-sky',
    label: 'Night sky',
    make: () => ({ type: 'linear', angle: 90, stops: [{ offset: 0, color: '#05021a', opacity: 1 }, { offset: 0.55, color: '#1e0b45', opacity: 1 }, { offset: 1, color: '#5b1667', opacity: 1 }] }),
  },
  {
    id: 'sunset',
    label: 'Sunset',
    make: () => ({ type: 'linear', angle: 90, stops: [{ offset: 0, color: '#2d1b69', opacity: 1 }, { offset: 0.6, color: '#d6336c', opacity: 1 }, { offset: 1, color: '#ffa94d', opacity: 1 }] }),
  },
];

/** Glow presets; `base` is the object's main colour. */
export const GLOW_PRESETS: Preset<Glow>[] = [
  { id: 'neon', label: 'Neon', make: (c) => ({ color: c, radius: 16, strength: 0.9 }) },
  { id: 'soft', label: 'Soft halo', make: (c) => ({ color: c, radius: 32, strength: 0.55 }) },
  { id: 'faint', label: 'Faint', make: (c) => ({ color: c, radius: 10, strength: 0.35 }) },
];

export const SHADOW_PRESETS: Preset<Shadow>[] = [
  { id: 'soft', label: 'Soft', make: () => ({ color: '#000000', blur: 16, opacity: 0.45, offsetX: 0, offsetY: 10 }) },
  { id: 'hard', label: 'Hard', make: () => ({ color: '#000000', blur: 0, opacity: 0.6, offsetX: 6, offsetY: 6 }) },
  { id: 'long', label: 'Long', make: () => ({ color: '#000000', blur: 24, opacity: 0.35, offsetX: 0, offsetY: 24 }) },
];

export const GRADE_PRESETS: { id: string; label: string; grade: Grade }[] = [
  { id: 'neutral', label: 'Neutral', grade: NEUTRAL_GRADE },
  { id: 'night', label: 'Night', grade: { brightness: 0.9, contrast: 1.1, saturation: 0.85, tint: '#3b5bdb', tintAmount: 0.2 } },
  { id: 'neon-noir', label: 'Neon noir', grade: { brightness: 0.95, contrast: 1.25, saturation: 1.2, tint: '#b84dff', tintAmount: 0.12 } },
  { id: 'warm', label: 'Warm', grade: { brightness: 1.05, contrast: 1.05, saturation: 1.1, tint: '#ff9e57', tintAmount: 0.15 } },
  { id: 'faded', label: 'Faded film', grade: { brightness: 1.05, contrast: 0.85, saturation: 0.7, tint: '#f3e5c0', tintAmount: 0.1 } },
];

export const VIGNETTE_PRESETS: { id: string; label: string; vignette: Vignette }[] = [
  { id: 'subtle', label: 'Subtle', vignette: { amount: 0.35, softness: 0.6, color: '#000000' } },
  { id: 'cinematic', label: 'Cinematic', vignette: { amount: 0.6, softness: 0.5, color: '#000000' } },
  { id: 'strong', label: 'Strong', vignette: { amount: 0.85, softness: 0.4, color: '#000000' } },
];
